import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, MinusCircle, Route, ShieldCheck, Wrench, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { WorkPageShell } from "@/components/WorkPageShell";
import { api } from "@/lib/api";
import { EnvironmentBadge, ProductionWarningBanner } from "@/components/EnvironmentBadge";
import { FrameworkBadge } from "@/components/FrameworkBadge";
import { formatDateTime } from "@/lib/datetime";
import { RefBadge } from "@/components/RefBadge";
import { ErrorDetails, AIRecoveryDetails } from "@/components/ErrorDetails";
import { StepScreenshot } from "@/components/StepScreenshot";

export default function RunResultPage() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [zoomShot, setZoomShot] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let active = true;
    // Track how many "settle" polls we've done after the run first
    // appeared terminal. When `status` becomes non-running but
    // `duration_ms` is still null, that's the race window between an
    // external write (a stray sweep + Iter 37.5's self-heal
    // reverting it) and the runner's actual finalize. In practice
    // the reversion happens within one poll, but on long steps
    // (heal-heavy steps like the `Click "9"` datepicker which take
    // ~22 s) the runner's next heartbeat may not fire for a while.
    //
    // Strategy: NEVER stop polling just because `status` looks
    // terminal — only stop when `duration_ms` is populated (the
    // runner's finalize marker). If the run has genuinely died,
    // the backend's stuck-run detector will set `duration_ms` to
    // a real value within ~2 min, and the user gets the correct
    // "Runner stopped responding" message.
    const fetchRun = async () => {
      try {
        // Iter 37.4 — cache-busting query param defeats browser HTTP
        // cache on the run-detail poll. Without this, some browsers
        // serve stale API responses from disk cache during long
        // polling loops, which caused the user's UI to lock on a
        // mid-run snapshot (e.g. "Run interrupted before step 22")
        // even after the runner's eager finalize wrote the real
        // error to Mongo.
        const { data } = await api.get(`/runs/${id}`, {
          params: { _ts: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });
        if (!active) return;
        setRun(data);
        // Iter 37.6 — poll until `duration_ms` is set. This is the
        // ONLY reliable "run is truly done" signal. `status` alone
        // can flip between running/failed multiple times during a
        // run due to the sweep + self-heal interaction on long steps.
        const finalized = data.duration_ms != null;
        if (!finalized) {
          pollRef.current = setTimeout(fetchRun, 1500);
        }
      } catch (err) {
        // 401 = session expired — the global api interceptor redirects to
        // /login. Stop polling so we don't flood the network with retries.
        if (err?.response?.status === 401) return;
        if (active) pollRef.current = setTimeout(fetchRun, 3000);
      }
    };
    fetchRun();
    return () => {
      active = false;
      clearTimeout(pollRef.current);
    };
  }, [id]);

  if (!run) {
    return (
      <WorkPageShell>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 text-sm text-[#64748B]">Loading…</div>
      </WorkPageShell>
    );
  }

  const passedCount = run.step_results.filter((s) => s.status === "passed").length;
  const skippedCount = run.step_results.filter((s) => s.status === "skipped").length;
  const interstitialSteps = run.step_results.filter((s) => s.interstitial_recovery?.engaged);

  return (
    <WorkPageShell backTo={`/tests/${run.test_id}`} backLabel="Back to test">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="run-result-page">
        {run.environment?.type === "production" && <ProductionWarningBanner />}
        {/* Summary header */}
        <div className="surface-card p-5 mb-8">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div>
              <div className="text-xs font-medium text-[#64748B]">Run report</div>
              <RefBadge value={run.test_ref} testId="run-test-ref" />
              <h1 className="font-['Geist'] font-semibold text-2xl tracking-tight mt-1 text-[#0F172A]" data-testid="run-test-name">
                {run.test_name}
              </h1>
            </div>
            <StatusBadge status={run.status} testId="run-status-badge" />
            {run.environment && <EnvironmentBadge type={run.environment.type} name={run.environment.name} testId="run-env-badge" />}
            <div>
              <span className="text-xs font-medium text-[#64748B]">Browser</span>
              <div className="text-sm font-semibold mt-0.5 text-[#0F172A] capitalize">{run.browser || "chromium"}</div>
            </div>
            <FrameworkBadge framework={run.detected_framework} testId="run-framework-badge" />
            <div>
              <span className="text-xs font-medium text-[#64748B]">Steps passed</span>
              <div className="text-sm font-semibold mt-0.5 text-[#0F172A]" data-testid="run-steps-passed">
                {passedCount}/{run.step_results.length}
              </div>
            </div>
            {skippedCount > 0 && (
              <div>
                <span className="text-xs font-medium text-[#64748B]">Skipped</span>
                <div className="text-sm font-semibold mt-0.5 text-[#B45309]" data-testid="run-steps-skipped">
                  {skippedCount}
                </div>
              </div>
            )}
            <div>
              <span className="text-xs font-medium text-[#64748B]">Duration</span>
              <div className="text-sm font-semibold mt-0.5 text-[#0F172A]">
                {run.duration_ms != null ? `${(run.duration_ms / 1000).toFixed(1)}s` : "—"}
              </div>
            </div>
            <div>
              <span className="text-xs font-medium text-[#64748B]">Started</span>
              <div className="text-sm font-semibold mt-0.5 text-[#0F172A]">
                {formatDateTime(run.started_at)}
              </div>
            </div>
          </div>
          {run.error && (
            <div className="mt-4 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3" data-testid="run-error-message">
              <span className="text-xs font-medium text-[#B91C1C]">Failure reason</span>
              <p className="font-mono text-xs text-[#7F1D1D] mt-1">{run.error}</p>
            </div>
          )}
          {run.status === "running" && (
            <div className="mt-4 flex items-center gap-2 text-xs text-[#1D4ED8]" data-testid="run-in-progress">
              <Loader2 size={13} className="animate-spin" />
              Executing… this page updates live
            </div>
          )}
        </div>

        {/* Heal events — only renders when the runner had to fall back on
            at least one step. Surfaces the strategy + old/new selector so
            owners can audit self-healing decisions. */}
        {(run.heal_events?.length || 0) > 0 && (
          <div className="surface-card p-5 mb-6" data-testid="run-heal-panel">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#10B981]/10 text-[#047857]">
                <Wrench size={14} />
              </span>
              <h2 className="text-sm font-semibold text-[#0F172A]">
                {run.heal_events.length} step{run.heal_events.length === 1 ? "" : "s"} healed
              </h2>
              <span className="text-[11px] text-[#64748B]">
                Probe fell back to a more stable selector for these steps.
              </span>
            </div>
            <ul className="space-y-2">
              {run.heal_events.map((h, idx) => (
                <li
                  key={idx}
                  data-testid={`heal-event-${idx}`}
                  className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-xs space-y-1"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[#0F172A]">Step {h.step_index + 1}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#10B981]/10 text-[#047857] text-[10px] font-medium">
                      {h.strategy}
                    </span>
                    {h.stable ? (
                      <span className="px-1.5 py-0.5 rounded bg-[#3B82F6]/10 text-[#1D4ED8] text-[10px] font-medium">
                        stable
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#B45309] text-[10px] font-medium">
                        fragile fallback
                      </span>
                    )}
                    <span className="font-mono text-[10px] text-[#94A3B8]">
                      conf {Math.round((h.confidence || 0) * 100)}%
                    </span>
                  </div>
                  <p className="font-mono text-[#64748B] truncate" title={h.old_selector}>
                    <span className="text-[#EF4444]">−</span> {h.old_selector || <em>(no primary)</em>}
                  </p>
                  <p className="font-mono text-[#0F172A] truncate" title={h.new_selector}>
                    <span className="text-[#10B981]">+</span> {h.new_selector}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Replay Plan Rewriter — rewrite/invalidation timeline. Renders
            when the dynamic execution plan diverged from the recorded
            steps (readonly fill→click, heals, overlay recoveries,
            invalidated + regenerated downstream steps). */}
        {(run.plan_rewrites?.length || 0) > 0 && (
          <div className="surface-card p-5 mb-6" data-testid="run-plan-rewrites-panel">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#6366F1]/10 text-[#4338CA]">
                <Route size={14} />
              </span>
              <h2 className="text-sm font-semibold text-[#0F172A]">
                Replay plan rewritten ({run.plan_rewrites.length} event{run.plan_rewrites.length === 1 ? "" : "s"})
              </h2>
              <span className="text-[11px] text-[#64748B]">
                Probe adapted the execution plan at runtime instead of replaying stale recorded actions.
              </span>
            </div>
            <ul className="space-y-2">
              {run.plan_rewrites.map((ev, idx) => (
                <li
                  key={idx}
                  data-testid={`plan-rewrite-event-${idx}`}
                  className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-xs flex items-center gap-2 flex-wrap"
                >
                  <span className="font-semibold text-[#0F172A]">Step {ev.index + 1}</span>
                  <span className="px-1.5 py-0.5 rounded bg-[#6366F1]/10 text-[#4338CA] text-[10px] font-medium">
                    {ev.kind}
                  </span>
                  {ev.state_change && (
                    <span className="px-1.5 py-0.5 rounded bg-[#F59E0B]/10 text-[#B45309] text-[10px] font-medium">
                      {ev.state_change}
                    </span>
                  )}
                  <span className="text-[#64748B]">{ev.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Surprise handling — unexpected interstitials (cookie banners,
            promo modals, confirmation dialogs) Probe detected during
            replay. Safe ones were dismissed and the recorded step was
            retried unchanged; unsafe ones were deliberately left alone. */}
        {interstitialSteps.length > 0 && (
          <div className="surface-card p-5 mb-6" data-testid="run-interstitial-panel">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#14B8A6]/10 text-[#0F766E]">
                <ShieldCheck size={14} />
              </span>
              <h2 className="text-sm font-semibold text-[#0F172A]">
                Surprise handling ({interstitialSteps.length} step{interstitialSteps.length === 1 ? "" : "s"})
              </h2>
              <span className="text-[11px] text-[#64748B]">
                Unexpected popups/dialogs detected during replay. Safe ones were dismissed; the recorded step was retried unchanged.
              </span>
            </div>
            <ul className="space-y-2">
              {interstitialSteps.map((s) => {
                const ir = s.interstitial_recovery;
                return (
                  <li
                    key={s.index}
                    data-testid={`interstitial-event-${s.index}`}
                    className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-xs space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0F172A]">Step {s.index + 1}</span>
                      <span className="px-1.5 py-0.5 rounded bg-[#14B8A6]/10 text-[#0F766E] text-[10px] font-medium">
                        {ir.outcome}
                      </span>
                      {ir.llm_used && (
                        <span className="px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] text-[10px] font-medium">
                          AI classified
                        </span>
                      )}
                    </div>
                    {(ir.dismissed || []).map((d, di) => (
                      <p key={di} className="text-[#0F766E]">
                        ✓ Dismissed <span className="font-medium">{d.kind || "blocker"}</span> via {d.method}
                        {d.label ? <> (&ldquo;{d.label}&rdquo;)</> : null}
                        <span className="text-[#94A3B8]"> — {d.source} classification</span>
                        {d.business_action && (
                          <span
                            className="ml-1 px-1 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] text-[10px] font-semibold uppercase"
                            data-testid={`interstitial-business-action-${s.index}`}
                          >
                            business action
                          </span>
                        )}
                      </p>
                    ))}
                    {(ir.skipped_unsafe || []).map((u, ui) => (
                      <p key={ui} className="text-[#B45309]">
                        ⚠ Left alone: <span className="font-medium">{u.kind || "blocker"}</span> — {u.reason}
                      </p>
                    ))}
                    {(ir.dismiss_failed || []).map((f, fi) => (
                      <p key={fi} className="text-[#B91C1C]">
                        ✗ Could not dismiss: <span className="font-medium">{f.kind || "blocker"}</span>
                      </p>
                    ))}
                    {ir.validation_recovery && (
                      <div
                        className="mt-1 rounded bg-[#F0FDFA] border border-[#99F6E4] px-2 py-1.5 space-y-0.5"
                        data-testid={`interstitial-validation-${s.index}`}
                      >
                        <p className="font-medium text-[#0F766E]">
                          🧩 Validation-guided field recovery — {ir.validation_recovery.outcome}
                          {ir.validation_recovery.extraction_source === "llm" && (
                            <span className="ml-1 px-1 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] text-[10px]">AI extracted</span>
                          )}
                        </p>
                        {(ir.validation_recovery.messages || []).slice(0, 2).map((m, mi) => (
                          <p key={mi} className="text-[#64748B] italic">&ldquo;{m}&rdquo;</p>
                        ))}
                        {(ir.validation_recovery.fields_recovered || []).map((f, fi) => (
                          <p key={fi} className="text-[#0F766E]">
                            ✓ {f.field} — {f.strategy}{f.detail ? ` (${f.detail})` : ""}
                            {f.strategy === "recorded-step-llm" && (
                              <span className="ml-1 px-1 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] text-[10px]">AI mapped</span>
                            )}
                            {f.ai_chosen && (
                              <span data-testid="vgfr-ai-chosen-pill" className="ml-1 px-1 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] text-[10px]">AI-chosen value</span>
                            )}
                          </p>
                        ))}
                        {(ir.validation_recovery.field_plans || [])
                          .filter((p) => p.strategy === "unmapped")
                          .map((p, pi) => (
                            <p key={pi} className="text-[#B45309]">⚠ {p.field} — {p.reason}</p>
                          ))}
                        {(ir.validation_recovery.fields_failed || []).map((f, fi) => (
                          <p key={fi} className="text-[#B91C1C]">✗ {f.field} — {f.reason}</p>
                        ))}
                        {ir.validation_recovery.retry_action?.description && (
                          <p className="text-[#334155]">↻ Retried: {ir.validation_recovery.retry_action.description}</p>
                        )}
                        {ir.validation_recovery.retry_action?.blocked && (
                          <p className="text-[#B45309]">⛔ {ir.validation_recovery.retry_action.blocked}</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Step timeline */}
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Step timeline</h2>
        <div className="space-y-0 mb-10" data-testid="step-timeline">
          {run.step_results.map((sr) => {
            const isSkipped = sr.status === "skipped";
            const isPassed = sr.status === "passed";
            // Three states: green=passed, red=failed, amber=skipped.
            // Skipped covers steps the runner deliberately no-op'd
            // (e.g. fill with recorder-noise `[object Object]` value).
            const dotBg = isPassed ? "bg-[#10B981]" : isSkipped ? "bg-[#F59E0B]" : "bg-[#EF4444]";
            const borderColor = isPassed ? "#10B981" : isSkipped ? "#F59E0B" : "#EF4444";
            return (
              <div
                key={sr.index}
                className="grid grid-cols-[20px_1fr] sm:grid-cols-[24px_1fr_140px] gap-3 sm:gap-4 border-l-2 pl-4 pb-6 relative"
                style={{ borderColor }}
                data-testid={`step-result-${sr.index}`}
              >
                <div
                  className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full flex items-center justify-center text-white ${dotBg}`}
                >
                  {isPassed ? (
                    <Check size={10} strokeWidth={3} />
                  ) : isSkipped ? (
                    <MinusCircle size={10} strokeWidth={3} />
                  ) : (
                    <X size={10} strokeWidth={3} />
                  )}
                </div>
                <span className="font-mono text-[11px] text-[#94A3B8] pt-0">{String(sr.index + 1).padStart(2, "0")}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-[#F1F5F9] text-[#475569] px-1.5 py-0.5 rounded">{sr.action}</span>
                    <span className="text-sm font-medium text-[#0F172A] truncate">{sr.description}</span>
                    <span className="font-mono text-[10px] text-[#94A3B8]">{sr.duration_ms}ms</span>
                    {isSkipped && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 rounded"
                        data-testid={`step-skipped-badge-${sr.index}`}
                      >
                        Skipped
                      </span>
                    )}
                    {/* Pixel-offset fallback warning: surface a "Low
                        stability" badge on click steps that fell back
                        to pixel-fraction targeting (no semantic
                        clickTarget). Detection is keyword-based on
                        the note so the UI doesn't need to know about
                        internal step shape. */}
                    {sr.note && /pixel-offset fallback/i.test(sr.note) && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 rounded border border-[#F59E0B]"
                        data-testid={`step-pixel-fallback-badge-${sr.index}`}
                        title="This click was replayed by pixel position because no semantic clickTarget was captured. Layout/CSS changes may break it. Re-record to harden."
                      >
                        Low stability
                      </span>
                    )}
                    {sr.ai_recovery?.outcome === "recovered" && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#EDE9FE] text-[#5B21B6] px-1.5 py-0.5 rounded border border-[#C4B5FD]"
                        data-testid={`step-ai-recovery-badge-${sr.index}`}
                        title="Deterministic replay failed. Probe's AI Recovery Engine diagnosed the page state, executed one verified recovery action, then replayed this step successfully."
                      >
                        🤖 AI Recovered
                      </span>
                    )}
                    {sr.interstitial_recovery?.step_retry === "passed" && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#CCFBF1] text-[#0F766E] px-1.5 py-0.5 rounded border border-[#99F6E4]"
                        data-testid={`step-interstitial-badge-${sr.index}`}
                        title="An unexpected popup/dialog was blocking this step. Probe's Surprise-Handling engine classified it as safe to dismiss, dismissed it, then replayed the original recorded step unchanged."
                      >
                        {sr.interstitial_recovery?.outcome === "validation-recovered"
                          ? "🧩 Validation recovered"
                          : "🛡️ Interstitial dismissed"}
                      </span>
                    )}
                    {sr.plan_rewrite && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#E0E7FF] text-[#4338CA] px-1.5 py-0.5 rounded border border-[#C7D2FE]"
                        data-testid={`step-plan-rewrite-badge-${sr.index}`}
                        title={sr.plan_rewrite.detail}
                      >
                        🔀 {sr.plan_rewrite.kind}
                      </span>
                    )}
                    {sr.plan?.regeneration?.ok && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#E0E7FF] text-[#4338CA] px-1.5 py-0.5 rounded border border-[#C7D2FE]"
                        data-testid={`step-plan-regenerated-badge-${sr.index}`}
                        title="This step's recorded selector was invalidated by an upstream plan rewrite. Probe regenerated it from recorded intent and verified the owner control's value change."
                      >
                        🔀 Regenerated from intent
                      </span>
                    )}
                    {sr.plan?.orphaned && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wide bg-[#FEE2E2] text-[#B91C1C] px-1.5 py-0.5 rounded border border-[#FCA5A5]"
                        data-testid={`step-plan-orphaned-badge-${sr.index}`}
                        title="This step was invalidated by an upstream plan rewrite and could not be regenerated from recorded intent."
                      >
                        Orphaned step
                      </span>
                    )}
                  </div>
                  {sr.selector && (
                    <p className="font-mono text-[11px] text-[#64748B] mt-1 truncate">{sr.selector}</p>
                  )}
                  {sr.note && (
                    <p
                      className={
                        sr.overlay_recovery?.passed || sr.ai_recovery?.outcome === "recovered" || sr.plan?.regeneration?.ok || sr.interstitial_recovery?.step_retry === "passed"
                          ? "mt-2 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] text-[12px] p-3 leading-relaxed"
                          : "mt-2 rounded-md border border-[#FCD34D] bg-[#FFFBEB] text-[#92400E] text-[12px] p-3 leading-relaxed"
                      }
                      data-testid={`step-note-${sr.index}`}
                    >
                      {sr.overlay_recovery?.passed && (
                        <span
                          className="inline-block mr-2 text-[10px] font-semibold uppercase tracking-wide bg-[#D1FAE5] text-[#065F46] px-1.5 py-0.5 rounded border border-[#A7F3D0]"
                          data-testid={`step-overlay-recovery-badge-${sr.index}`}
                        >
                          {sr.overlay_recovery?.step_kind === "navigation"
                            ? "🧭 Overlay navigation"
                            : "🎯 Overlay recovery"}
                        </span>
                      )}
                      {sr.note}
                    </p>
                  )}
                  {sr.ai_recovery && sr.status === "passed" && (
                    <AIRecoveryDetails recovery={sr.ai_recovery} index={sr.index} />
                  )}
                  {sr.error && (
                    <ErrorDetails step={sr} run={run} />
                  )}
                </div>
                {sr.screenshot && (
                  <StepScreenshot step={sr} onZoom={setZoomShot} />
                )}
              </div>
            );
          })}
          {run.step_results.length === 0 && (
            <div className="surface-card p-6 text-sm text-[#64748B]">Waiting for first step…</div>
          )}
        </div>

        {/* Logs */}
        <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Execution logs</h2>
        <div className="rounded-lg bg-[#0F172A] text-[#E2E8F0] font-mono text-[11px] p-4 max-h-72 overflow-y-auto leading-relaxed border border-[#1E293B]" data-testid="execution-logs">
          {run.logs.length === 0 ? (
            <span className="text-[#64748B]">No logs yet.</span>
          ) : (
            run.logs.map((l, i) => <div key={i}>{l}</div>)
          )}
        </div>

        {/* Screenshot zoom */}
        <Dialog open={!!zoomShot} onOpenChange={(o) => !o && setZoomShot(null)}>
          <DialogContent className="max-w-5xl rounded-xl border-[#E5E7EB] p-2" aria-describedby={undefined}>
            <DialogTitle className="sr-only">Step screenshot</DialogTitle>
            {zoomShot && <StepScreenshot step={zoomShot} variant="zoom" />}
          </DialogContent>
        </Dialog>
      </div>
    </WorkPageShell>
  );
}
